import getSymbolFromCurrency from 'currency-symbol-map';
import { useState } from 'react';

import { applyDiscountCode, removeDiscountCode } from 'api/checkout/checkout';
import { useClient } from 'hooks/useClient/useClient';
import { useLocale } from 'hooks/useLocale/useLocale';
import { Button, TextField, Typography } from 'ui/atoms';

import { useStyles } from './DiscountCodeCard.styles';
import { DiscountCodeCardProps } from './DiscountCodeCard.types';

export const DiscountCodeCard = ({ paymentId, retailDetail, disabled, onDiscountChanged }: DiscountCodeCardProps) => {
  const styles = useStyles();
  const client = useClient();
  const { formatMessage } = useLocale();

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setLoading] = useState(false);

  const appliedCode = retailDetail.discountCode;
  const discountAmount = retailDetail.discountAmount || 0;

  const handleApply = async () => {
    setError('');
    setLoading(true);
    try {
      await applyDiscountCode({ client, paymentId, code: code.trim() });
      await onDiscountChanged();
      setCode('');
    } catch (e: any) {
      // The api client's interceptor rejects with error.response, so the body is one level up.
      setError(
        e?.status === 429
          ? formatMessage({ id: 'checkout.payment_page.discount_code.too_many_attempts' })
          : e?.data?.message || formatMessage({ id: 'checkout.payment_page.discount_code.invalid' }),
      );
    }
    setLoading(false);
  };

  const handleRemove = async () => {
    setError('');
    setLoading(true);
    try {
      await removeDiscountCode({ client, paymentId });
      if (retailDetail.totalAmount === 0) {
        // The free order's payment was cancelled, so the new one needs a fresh Stripe session.
        window.location.reload();

        return;
      }
      await onDiscountChanged();
    } catch (e: any) {
      setError(e?.data?.message || formatMessage({ id: 'checkout.payment_page.discount_code.invalid' }));
    }
    setLoading(false);
  };

  if (appliedCode) {
    return (
      <div className={styles.container}>
        <div className={styles.discountRow}>
          <div className={styles.discountLabel}>
            <Typography variant="h6">
              {formatMessage({ id: 'checkout.payment_page.discount_code.applied' }, { code: appliedCode })}
            </Typography>
            <Button
              variant="text"
              size="small"
              className={styles.removeButton}
              disabled={disabled || isLoading}
              onClick={handleRemove}
            >
              {formatMessage({ id: 'checkout.payment_page.discount_code.remove' })}
            </Button>
          </div>
          <Typography>{`-${getSymbolFromCurrency(retailDetail.currency)}${discountAmount.toFixed(2)}`}</Typography>
        </div>
        {!!error && <Typography className={styles.error}>{error}</Typography>}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.form}>
        <TextField
          size="small"
          variant="outlined"
          className={styles.input}
          value={code}
          disabled={disabled || isLoading}
          error={!!error}
          helperText={error}
          placeholder={formatMessage({ id: 'checkout.payment_page.discount_code.placeholder' })}
          onChange={(event) => setCode(event.target.value)}
        />
        <Button
          variant="outlined"
          className={styles.applyButton}
          disabled={disabled || isLoading || code.trim().length < 3}
          onClick={handleApply}
        >
          {formatMessage({ id: 'checkout.payment_page.discount_code.apply' })}
        </Button>
      </div>
    </div>
  );
};
